import { useEffect, useState } from 'react';
import { Form, Input, Select, InputNumber, Row, Col, Switch, Typography } from 'antd';
import { studioLookups } from '../itemsApi';
import { getNature } from '../productStudioModes';
import {
  GLASS_TYPES, GLASS_PROCESS_TYPES,
  PROFILE_MATERIALS,
  HARDWARE_TYPES, HARDWARE_CATEGORIES, HARDWARE_FINISH_TYPES,
  JOBWORK_TYPES, JOBWORK_PROCESS_TYPES, JOBWORK_CHARGE_BASES, JOBWORK_RATE_BASES,
  PUBLISH_STATUSES, VISIBILITIES, SLUG_PATTERN_SRC,
  humaniseEnum
} from '../studioConstants';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Tiny nature-specific technical form. Renders only the mini-field
// set defined in `productStudioModes.js → ITEM_NATURES[*].mini`.
//
// Used inside Quick Create (single-card layout) and inside Guided
// Step 4. The full extension panels remain available in Advanced
// mode (TechnicalDetailsStep.jsx, unchanged).
//
// Field names match what `pluck*Values` in the studio page already
// expects — this keeps save orchestration unchanged.
// ─────────────────────────────────────────────────────────────────

function GlassMini({ disabled, polishTypes }) {
  return (
    <Row gutter={[12, 0]}>
      <Col xs={24} md={8}>
        <Form.Item label="Thickness (mm)" name="thicknessMm">
          <InputNumber min={0} step={0.5} precision={3} disabled={disabled} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Glass type" name="glassType">
          <Select allowClear disabled={disabled}
            options={GLASS_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Colour" name="glassColor">
          <Input disabled={disabled} placeholder="e.g. Clear, Bronze" />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Process" name="processType">
          <Select allowClear disabled={disabled}
            options={GLASS_PROCESS_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={16}>
        <Form.Item label="Polish type" name="polishTypeId">
          <Select allowClear showSearch optionFilterProp="label" disabled={disabled}
            options={polishTypes.map((p) => ({
              value: p.id,
              label: `${p.polishTypeCode || p.polish_type_code} — ${p.polishTypeName || p.polish_type_name}`
            }))} />
        </Form.Item>
      </Col>
    </Row>
  );
}

function ProfileMini({ disabled }) {
  return (
    <Row gutter={[12, 0]}>
      <Col xs={24} md={8}>
        <Form.Item label="Material" name="profileMaterial">
          <Select allowClear disabled={disabled}
            options={PROFILE_MATERIALS.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Series" name="profileSeries">
          <Input disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Colour" name="profileColor">
          <Input disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Length (mm)" name="lengthMm">
          <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Brand" name="brand">
          <Input disabled={disabled} />
        </Form.Item>
      </Col>
    </Row>
  );
}

function HardwareMini({ disabled }) {
  return (
    <Row gutter={[12, 0]}>
      <Col xs={24} md={8}>
        <Form.Item label="Type" name="hardwareType">
          <Select allowClear showSearch optionFilterProp="label" disabled={disabled}
            options={HARDWARE_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Category" name="hardwareCategory">
          <Select allowClear disabled={disabled}
            options={HARDWARE_CATEGORIES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Finish" name="hwFinishType">
          <Select allowClear disabled={disabled}
            options={HARDWARE_FINISH_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Brand" name="hwBrand">
          <Input disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Model no." name="hwModelNo">
          <Input disabled={disabled} />
        </Form.Item>
      </Col>
    </Row>
  );
}

function JobworkMini({ disabled }) {
  return (
    <Row gutter={[12, 0]}>
      <Col xs={24} md={8}>
        <Form.Item label="Type" name="jobworkType">
          <Select allowClear disabled={disabled}
            options={JOBWORK_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Process" name="jobworkProcessType">
          <Select allowClear showSearch optionFilterProp="label" disabled={disabled}
            options={JOBWORK_PROCESS_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Charge basis" name="chargeBasis">
          <Select allowClear disabled={disabled}
            options={JOBWORK_CHARGE_BASES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Rate basis" name="rateBasis">
          <Select allowClear disabled={disabled}
            options={JOBWORK_RATE_BASES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Minimum charge" name="minimumCharge">
          <InputNumber min={0} step={1} disabled={disabled} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>
  );
}

function EcommerceMini({ disabled }) {
  return (
    <Row gutter={[12, 0]}>
      <Col xs={24} md={12}>
        <Form.Item label="Ecommerce title" name="ecommerceTitle" rules={[{ max: 180 }]}>
          <Input disabled={disabled} placeholder="Public-facing product title." />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          label="Slug"
          name="ecommerceSlug"
          normalize={(v) => (v === undefined || v === null || v === '')
            ? v : String(v).trim().toLowerCase()}
          rules={[
            { max: 180 },
            { pattern: new RegExp(SLUG_PATTERN_SRC),
              message: 'Lowercase letters, digits and single hyphens only.' }
          ]}
        >
          <Input disabled={disabled} placeholder="e.g. 4mm-toughened-clear-glass" />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Publish status" name="publishStatus">
          <Select disabled={disabled}
            options={PUBLISH_STATUSES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Visibility" name="visibility">
          <Select disabled={disabled}
            options={VISIBILITIES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Visible online" name="isVisibleOnline" valuePropName="checked">
          <Switch disabled={disabled} />
        </Form.Item>
      </Col>
    </Row>
  );
}

const PANELS = {
  glass:     GlassMini,
  profile:   ProfileMini,
  hardware:  HardwareMini,
  jobwork:   JobworkMini,
  ecommerce: EcommerceMini
};

export default function RelevantTechnicalMiniForm({ natureKey, disabled }) {
  const nature = getNature(natureKey);
  const [polishTypes, setPolishTypes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (natureKey !== 'glass') return undefined;
    (async () => {
      try {
        const rows = await studioLookups.polishTypes({ q: '', isActive: true, limit: 200 });
        if (!cancelled) setPolishTypes(Array.isArray(rows) ? rows : []);
      } catch (_e) { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, [natureKey]);

  if (!nature || !nature.extension) return null;
  const Panel = PANELS[nature.extension];
  if (!Panel) return null;

  return (
    <div className="erp-studio-mini">
      <Text strong style={{ fontSize: 13 }}>{nature.label} — quick details</Text>
      <p style={{ margin: '2px 0 10px', fontSize: 11.5, color: '#64748B' }}>
        Only the most-used fields are shown here. Open the item in Advanced
        Create later to fill all technical specs.
      </p>
      <Panel disabled={disabled} polishTypes={polishTypes} />
    </div>
  );
}
